import type { FC } from "react";

import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import type { Client as UrqlClient } from "urql";

import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ClipboardModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  CsvExportModule,
  DateFilterModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  GridStateModule,
  GroupFilterModule,
  LicenseManager,
  MasterDetailModule,
  ModuleRegistry,
  PaginationModule,
  PivotModule,
  QuickFilterModule,
  RenderApiModule,
  RowApiModule,
  RowAutoHeightModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  RowSelectionModule,
  RowStyleModule,
  ScrollApiModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
  SideBarModule,
  StatusBarModule,
  TooltipModule,
  ValidationModule,
  ValueCacheModule,
} from "ag-grid-enterprise";
import { message } from "antd";
import { Provider } from "urql";

import {
  ClientSDKReactQueryProvider,
  handleError,
} from "@alaffia-technology-solutions/client-sdk-react-query";
import { DefaultMarkdownContextProvider } from "@alaffia-technology-solutions/components";
import {
  SidebarInset,
  SidebarProvider,
  Sonner,
  TooltipProvider,
} from "@alaffia-technology-solutions/ds";

import { AlaffiaLogin } from "../../alaffiaClient/alaffiaLogin";
import { useAlaffiaClient } from "../../alaffiaClient/useAlaffiaClient";
import { useLoginStatus } from "../../alaffiaClient/useLoginStatus";
import { ErrorBoundary } from "../../common/error/errorBoundary";
import { AUTH_CALLBACK_PATH } from "../auth/authRoutes";
import { PdfViewerProvider } from "../documentViewer/PdfViewerProvider";
import { User } from "../user/User";
import { DesignSystemProvider } from "./design/designSystemProvider";
import { Maintenance } from "./maintenance";
import { Navigation } from "./navigation";
import { AuthCallbackRoute, Routes } from "./routes";
import { UnhandledRejectionsCatch } from "./unhandled-rejections-catch";

ModuleRegistry.registerModules([
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ClipboardModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  CsvExportModule,
  DateFilterModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  GridStateModule,
  GroupFilterModule,
  MasterDetailModule,
  PaginationModule,
  PivotModule,
  QuickFilterModule,
  RenderApiModule,
  RowApiModule,
  RowAutoHeightModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  RowSelectionModule,
  RowStyleModule,
  ScrollApiModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
  SideBarModule,
  StatusBarModule,
  TooltipModule,
  ValidationModule,
  ValueCacheModule,
]);

LicenseManager.setLicenseKey(
  `Using_this_{AG_Grid}_Enterprise_key_{AG-098486}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Alaffia_Health}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{alaffia}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{alaffia}_need_to_be_licensed___{alaffia}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{5_June_2027}____[v3]_[01]_MTgxMjE1MDAwMDAwMA==1a0bc95dee3fc6e0cd5e10829aaee0b9`,
);

const maintenanceMessage = process.env.MAINTENANCE_MESSAGE ?? null;
const siteOffline = !!maintenanceMessage; // boolean flag for other (future) 'down/limited' status messages

const baseUrl = window.location.origin;

const App: FC = () => {
  const alaffiaClient = useAlaffiaClient();
  const { loggedIn } = useLoginStatus();
  const location = useLocation();
  const isAuthCallback = location.pathname === AUTH_CALLBACK_PATH;

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            const meta = query?.options.meta;
            if (!meta?.supressGlobalErrorHandler) {
              handleError(error, (title, { description }) => {
                if (description === "refreshToken: must login first!") {
                  return;
                }

                message.error(`${title}: ${description}`);
              });
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            const meta = mutation?.options.meta;
            if (!meta?.supressGlobalErrorHandler) {
              handleError(error, (title, { description }) => {
                if (description === "refreshToken: must login first!") {
                  return;
                }

                message.error(`${title}: ${description}`);
              });
            }
          },
        }),
      }),
  );
  const [graphqlClient, setGraphqlClient] = useState<UrqlClient>();

  useEffect(() => {
    if (alaffiaClient) {
      const gqlc = alaffiaClient.api.getGraphqlClient();
      setGraphqlClient(gqlc);
    }
  }, [alaffiaClient, setGraphqlClient]);

  const [_creatingReport, setCreatingReport] = React.useState(false);

  const lastLoggedIn = useRef(loggedIn);
  useEffect(() => {
    lastLoggedIn.current = loggedIn;
  }, [loggedIn]);

  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    return <div>No API URL</div>;
  }

  const foxitLicenseKey = process.env.ALAFFIA_FOXIT_KEY;

  if (!foxitLicenseKey) {
    return <div>No Foxit license key</div>;
  }

  const foxitLicenseSN = process.env.ALAFFIA_FOXIT_SN;

  if (!foxitLicenseSN) {
    return <div>No Foxit license SN</div>;
  }

  if (!graphqlClient) {
    return <></>;
  }

  return (
    <ErrorBoundary boundaryDescription="App Root">
      <Provider value={graphqlClient}>
        <UnhandledRejectionsCatch>
          <ClientSDKReactQueryProvider
            alaffiaClient={alaffiaClient}
            queryClient={queryClient}
          >
            <TooltipProvider>
              <Sonner />
              <PdfViewerProvider
                baseUrl={baseUrl}
                foxitLicenseSN={foxitLicenseSN}
                foxitLicenseKey={foxitLicenseKey}
              >
                <ReactQueryDevtools initialIsOpen={false} />
                <DesignSystemProvider>
                  {siteOffline ? (
                    <Maintenance message={maintenanceMessage} />
                  ) : (
                    <User
                      id={(loggedIn && alaffiaClient?.auth.userId) ?? null}
                      loggedIn={loggedIn}
                    >
                      <DefaultMarkdownContextProvider>
                        <SidebarProvider defaultOpen={false}>
                          {!siteOffline && loggedIn && (
                            <div
                              id="app_layout_sider"
                              className="!af-bg-transparent"
                            >
                              <Navigation
                                {...{
                                  setCreatingReport,
                                }}
                              />
                            </div>
                          )}
                          <SidebarInset>
                            <div
                              id="app_layout_top_wrapper"
                              className="af-flex af-h-full af-w-full af-flex-col"
                            >
                              <div
                                id="app_layout_content_container"
                                className="af-h-full af-max-h-full af-min-h-full af-flex-1"
                              >
                                <div
                                  id="app_layout_content"
                                  className="af-h-full af-max-h-full af-min-h-full"
                                >
                                  {isAuthCallback ? (
                                    <AuthCallbackRoute />
                                  ) : (
                                    <AlaffiaLogin>
                                      <Routes />
                                    </AlaffiaLogin>
                                  )}
                                </div>
                              </div>
                            </div>
                          </SidebarInset>
                        </SidebarProvider>
                      </DefaultMarkdownContextProvider>
                    </User>
                  )}
                </DesignSystemProvider>
              </PdfViewerProvider>
            </TooltipProvider>
          </ClientSDKReactQueryProvider>
        </UnhandledRejectionsCatch>
      </Provider>
    </ErrorBoundary>
  );
};

export { App };
